import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { api } from '../api';
import {
  ArrowLeft,
  Package,
  History,
  CheckCircle2,
  Circle,
  ImageOff,
  Pencil,
  Boxes,
} from 'lucide-react';

function StatusBadge({ status }) {
  if (status === 'available')
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
        <CheckCircle2 size={11} /> Available
      </span>
    );
  if (status === 'checked-out')
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20">
        <Circle size={11} /> Checked Out
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
      <Circle size={11} /> Partial
    </span>
  );
}

export default function ItemHistory() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { items, projects, itemAvailableQty, itemStatus, categoryColor } = useApp();
  const item = items.find((i) => i.code === code);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const tx = await api.listTransactions({ item_code: code });
        if (!cancelled) setTransactions(tx);
      } catch {
        if (!cancelled) setTransactions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const outOnProjects = useMemo(() => {
    if (!item) return [];
    return projects
      .filter((p) => p.status === 'active' && (p.packed?.[item.code] || 0) > 0)
      .map((p) => ({ project: p, qty: p.packed[item.code] }));
  }, [projects, item]);

  if (!item) {
    return (
      <div className="p-8">
        <p className="text-neutral-400 mb-3">Item not found.</p>
        <Link to="/inventory" className="text-amber-400 hover:underline text-sm">
          Back to inventory
        </Link>
      </div>
    );
  }

  const avail = itemAvailableQty(item.code);
  const status = itemStatus(item.code);

  return (
    <div className="p-5 max-w-4xl">
      <button
        onClick={() => navigate('/inventory')}
        className="text-xs text-neutral-500 hover:text-white inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft size={13} /> Inventory
      </button>

      {/* Header card */}
      <div className="surface rounded-lg p-5 mb-5">
        <div className="flex items-start gap-5">
          {!imgErr && item.image ? (
            <img
              src={item.image}
              alt={item.name}
              onError={() => setImgErr(true)}
              className="h-24 w-32 object-cover rounded border border-neutral-800 shrink-0"
            />
          ) : (
            <div className="h-24 w-32 rounded bg-neutral-900 border border-neutral-800 grid place-items-center text-neutral-600 shrink-0">
              <ImageOff size={24} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1 font-mono">
              {item.code}
            </div>
            <h1 className="text-2xl font-bold text-white leading-tight mb-2">{item.name}</h1>
            <div className="flex items-center gap-2 flex-wrap text-xs text-neutral-400">
              <span className="inline-flex items-center gap-1.5">
                <span className="cat-dot" style={{ background: categoryColor(item.category) }} />
                {item.category}
              </span>
              <span className="text-neutral-700">·</span>
              <span>{item.location}</span>
              <span className="text-neutral-700">·</span>
              <StatusBadge status={status} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-neutral-900">
          <div>
            <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1">Total Units</div>
            <div className="text-2xl font-bold text-white">{item.totalQty}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1">Available</div>
            <div className="text-2xl font-bold text-emerald-400">{avail}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1">QR / Scan Code</div>
            <div className="font-mono text-sm text-white bg-neutral-950 border border-neutral-800 rounded px-2 py-1 inline-block mt-0.5">
              {item.qrCode || item.code}
            </div>
          </div>
        </div>
      </div>

      {/* Currently on projects */}
      {outOnProjects.length > 0 && (
        <div className="surface rounded-lg overflow-hidden mb-5">
          <div className="px-4 py-2.5 border-b border-neutral-900 flex items-center gap-2 bg-neutral-950">
            <Boxes size={13} className="text-amber-400" />
            <span className="text-xs font-semibold text-white tracking-wider uppercase">
              Currently on projects ({outOnProjects.length})
            </span>
          </div>
          <div className="divide-y divide-neutral-900">
            {outOnProjects.map(({ project, qty }) => (
              <button
                key={project.id}
                onClick={() => navigate(`/projects/${project.id}`)}
                className="w-full flex items-center gap-3 px-4 py-3 row-hover text-left"
              >
                <Package size={13} className="text-amber-400 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm text-white">{project.name}</div>
                  <div className="text-[11px] text-neutral-500">
                    {project.client || '—'} · {project.location || '—'}
                  </div>
                </div>
                <span className="text-[11px] text-amber-400 font-medium">
                  {qty} unit{qty === 1 ? '' : 's'}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div className="surface rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b border-neutral-900 flex items-center gap-2 bg-neutral-950">
          <History size={13} className="text-amber-400" />
          <span className="text-xs font-semibold text-white tracking-wider uppercase">
            Full History ({transactions.length})
          </span>
        </div>
        {loading ? (
          <div className="p-6 text-center text-xs text-neutral-500">Loading…</div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center text-xs text-neutral-500">No activity yet for this item.</div>
        ) : (
          <div className="divide-y divide-neutral-900">
            {transactions.map((t) => {
              const proj = projects.find((p) => p.id === t.projectId);
              return (
                <div key={t.id} className="px-4 py-2.5 flex items-center gap-3 text-xs">
                  <span
                    className={`text-[10px] uppercase tracking-wider font-semibold w-16 ${
                      t.type === 'pack' ? 'text-amber-400' : 'text-emerald-400'
                    }`}
                  >
                    {t.type}
                  </span>
                  {t.kitCode && (
                    <span className="text-[10px] font-mono bg-neutral-900 px-1.5 py-0.5 rounded text-neutral-400 border border-neutral-800">
                      via {t.kitCode}
                    </span>
                  )}
                  {proj ? (
                    <button
                      onClick={() => navigate(`/projects/${proj.id}`)}
                      className="text-neutral-300 hover:text-amber-400 flex-1 truncate text-left"
                    >
                      {proj.name}
                    </button>
                  ) : (
                    <span className="text-neutral-500 flex-1 truncate">(deleted project)</span>
                  )}
                  <span className="text-neutral-500">{t.userEmail || '—'}</span>
                  <span className="text-neutral-600">{new Date(t.ts).toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
